import { useEffect, useState } from "react";
import axios from "axios";
import { PlusCircle, Pencil, Trash2, Sparkles, Loader2, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { useLanguage } from "@/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full rounded-xl border border-slate-700 bg-slate-950/70 px-4 py-3 text-sm text-slate-100 placeholder:text-[#5C6A87] focus:outline-none focus:ring-2 focus:ring-[#3A5CC8] focus:border-blue-500 transition-colors duration-200";

const emptyForm = { title: "", slug: "", category: "", excerpt: "", content: "", cover_image: "", published: false, lang: "fr", translation_key: "", country: "" };

export const AdminBlog = ({ token }) => {
  const { t } = useLanguage();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null = list, "new" or post id
  const [form, setForm] = useState(emptyForm);
  const [topic, setTopic] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const headers = { Authorization: `Bearer ${token}` };

  const load = () =>
    axios.get(`${API}/admin/blog`, { headers })
      .then(({ data }) => setPosts(data))
      .catch(() => toast.error(t("Chargement impossible.")))
      .finally(() => setLoading(false));

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const set = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  const startNew = () => {
    setForm(emptyForm);
    setTopic("");
    setEditing("new");
  };

  const startEdit = (post) => {
    setForm({
      title: post.title, slug: post.slug, category: post.category || "",
      excerpt: post.excerpt || "", content: post.content || "",
      cover_image: post.cover_image || "", published: post.published,
      lang: post.lang || "fr", translation_key: post.translation_key || "", country: post.country || "",
    });
    setTopic("");
    setEditing(post.id);
  };

  const draftWithAi = async () => {
    setAiLoading(true);
    try {
      const { data } = await axios.post(`${API}/ai/draft-article`, { topic, category: form.category }, { headers });
      setForm((f) => ({ ...f, title: data.title, excerpt: data.excerpt, content: data.content }));
      toast.success(t("Brouillon généré — relisez et ajustez avant publication."));
    } catch (e) {
      const detail = e.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "L'assistant n'a pas pu rédiger. Réessayez.");
    } finally {
      setAiLoading(false);
    }
  };

  const save = async () => {
    setSaving(true);
    try {
      if (editing === "new") {
        await axios.post(`${API}/admin/blog`, form, { headers });
        toast.success(t("Article créé."));
      } else {
        await axios.put(`${API}/admin/blog/${editing}`, form, { headers });
        toast.success(t("Article mis à jour."));
      }
      setEditing(null);
      load();
    } catch (e) {
      toast.error(t("Enregistrement impossible. Réessayez."));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (post) => {
    if (!window.confirm(`Supprimer « ${post.title} » ?`)) return;
    try {
      await axios.delete(`${API}/admin/blog/${post.id}`, { headers });
      toast.success(t("Article supprimé."));
      load();
    } catch (e) {
      toast.error(t("Suppression impossible."));
    }
  };

  if (editing) {
    return (
      <div data-testid="blog-editor" className="mt-8 rounded-2xl border border-slate-800 bg-slate-900/60 p-6 sm:p-8 space-y-5">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <h2 className="font-heading text-xl font-semibold">{editing === "new" ? t("Nouvel article") : t("Modifier l'article")}</h2>
          <button
            type="button"
            data-testid="blog-editor-back"
            onClick={() => setEditing(null)}
            className="text-sm text-[#93A0BC] hover:text-white transition-colors duration-200"
          >
            {t("Retour à la liste")}
          </button>
        </div>

        <div className="rounded-xl border border-slate-700 bg-slate-950/60 p-4">
          <p className="text-sm font-semibold inline-flex items-center gap-2">
            <Sparkles className="w-4 h-4 text-blue-500" /> {t("Rédaction assistée Devisto AI")}
          </p>
          <div className="mt-3 flex flex-col sm:flex-row gap-3">
            <input
              data-testid="blog-ai-topic"
              className={inputCls}
              placeholder="Ex. : Prix d'une pompe à chaleur en Belgique en 2026"
              value={topic}
              onChange={(e) => setTopic(e.target.value)}
            />
            <button
              type="button"
              data-testid="blog-ai-draft-btn"
              disabled={aiLoading || topic.trim().length < 5}
              onClick={draftWithAi}
              className="shrink-0 inline-flex items-center justify-center gap-2 rounded-full bg-[#3A5CC8] px-6 py-3 text-sm font-semibold text-white hover:bg-[#3A5CC8] disabled:opacity-40 disabled:pointer-events-none transition-colors duration-200"
            >
              {aiLoading ? <><Loader2 className="w-4 h-4 animate-spin" /> {t("Rédaction…")}</> : t("Rédiger le brouillon")}
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          <div>
            <label htmlFor="blog-title" className="block text-sm font-medium text-slate-300 mb-2">{t("Titre")}</label>
            <input id="blog-title" data-testid="blog-title" className={inputCls} value={form.title} onChange={(e) => set("title", e.target.value)} />
          </div>
          <div>
            <label htmlFor="blog-slug" className="block text-sm font-medium text-slate-300 mb-2">
              {t("Slug (URL)")} {editing !== "new" && <span className="text-[#5C6A87]">{t("— figé pour préserver le référencement")}</span>}
            </label>
            <input id="blog-slug" data-testid="blog-slug" className={inputCls} placeholder={t("Auto si vide")} value={form.slug} onChange={(e) => set("slug", e.target.value)} disabled={editing !== "new"} />
          </div>
          <div>
            <label htmlFor="blog-category" className="block text-sm font-medium text-slate-300 mb-2">{t("Catégorie")}</label>
            <input id="blog-category" data-testid="blog-category" className={inputCls} placeholder="Ex. : Chauffage" value={form.category} onChange={(e) => set("category", e.target.value)} />
          </div>
          <div>
            <label htmlFor="blog-cover" className="block text-sm font-medium text-slate-300 mb-2">{t("Image de couverture (URL)")}</label>
            <input id="blog-cover" data-testid="blog-cover" className={inputCls} placeholder="https://…" value={form.cover_image} onChange={(e) => set("cover_image", e.target.value)} />
          </div>
          <div>
            <label htmlFor="blog-lang" className="block text-sm font-medium text-slate-300 mb-2">{t("Langue de l'article")}</label>
            <select id="blog-lang" data-testid="blog-lang" className={inputCls} value={form.lang} onChange={(e) => set("lang", e.target.value)}>
              <option value="fr">Français</option>
              <option value="nl">Nederlands</option>
              <option value="es">Español</option>
            </select>
          </div>
          <div>
            <label htmlFor="blog-country" className="block text-sm font-medium text-slate-300 mb-2">{t("Pays ciblé")}</label>
            <select id="blog-country" data-testid="blog-country" className={inputCls} value={form.country} onChange={(e) => set("country", e.target.value)}>
              <option value="">{t("Belgique (par défaut)")}</option>
              <option value="BE">Belgique</option>
              <option value="FR">France</option>
              <option value="ES">España</option>
              <option value="LU">Luxembourg</option>
            </select>
          </div>
          <div>
            <label htmlFor="blog-translation" className="block text-sm font-medium text-slate-300 mb-2">{t("Version liée (autre langue)")}</label>
            <select
              id="blog-translation"
              data-testid="blog-translation"
              className={inputCls}
              value={form.translation_key}
              onChange={(e) => set("translation_key", e.target.value)}
            >
              <option value="">{t("Aucune (article indépendant)")}</option>
              {posts
                .filter((p) => (p.lang || "fr") !== form.lang && p.id !== editing)
                .map((p) => (
                  <option key={p.id} value={p.translation_key || p.id}>
                    {(p.lang || "fr").toUpperCase()} — {p.title}
                  </option>
                ))}
            </select>
          </div>
        </div>
        <div>
          <label htmlFor="blog-excerpt" className="block text-sm font-medium text-slate-300 mb-2">{t("Résumé (SEO)")}</label>
          <textarea id="blog-excerpt" data-testid="blog-excerpt" rows={2} className={inputCls} value={form.excerpt} onChange={(e) => set("excerpt", e.target.value)} />
        </div>
        <div>
          <label htmlFor="blog-content" className="block text-sm font-medium text-slate-300 mb-2">
            {t("Contenu")} <span className="text-[#5C6A87]">{t("(markdown léger : ## titre, - liste, **gras**)")}</span>
          </label>
          <textarea id="blog-content" data-testid="blog-content" rows={14} className={inputCls} value={form.content} onChange={(e) => set("content", e.target.value)} />
        </div>
        <label className="inline-flex items-center gap-3 text-sm text-slate-300 cursor-pointer">
          <input
            type="checkbox"
            data-testid="blog-published"
            checked={form.published}
            onChange={(e) => set("published", e.target.checked)}
            className="w-4 h-4 accent-blue-600"
          />
          {t("Publier l'article (visible sur /blog)")}
        </label>
        <div>
          <button
            type="button"
            data-testid="blog-save-btn"
            disabled={saving || !form.title.trim() || !form.content.trim()}
            onClick={save}
            className="inline-flex items-center justify-center gap-2 rounded-full bg-[#3A5CC8] px-8 py-3.5 text-sm font-semibold text-white hover:bg-[#3A5CC8] disabled:opacity-40 disabled:pointer-events-none transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-[#3A5CC8]"
          >
            {saving ? t("Enregistrement…") : t("Enregistrer l'article")}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="blog-tab" className="mt-8">
      <button
        type="button"
        data-testid="blog-new-btn"
        onClick={startNew}
        className="inline-flex items-center gap-2 rounded-full bg-[#3A5CC8] px-6 py-3 text-sm font-semibold text-white hover:bg-[#3A5CC8] transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-[#3A5CC8]"
      >
        <PlusCircle className="w-4 h-4" /> {t("Nouvel article")}
      </button>

      <div className="mt-6 space-y-4">
        {loading && <p className="text-sm text-[#5C6A87]">{t("Chargement…")}</p>}
        {!loading && posts.length === 0 && <p className="text-sm text-[#5C6A87]">{t("Aucun article pour le moment.")}</p>}
        {posts.map((post) => (
          <article key={post.id} data-testid={`blog-row-${post.slug}`} className="rounded-2xl border border-slate-800 bg-slate-900/60 p-6 flex flex-wrap items-center gap-4 justify-between">
            <div className="min-w-0">
              <div className="flex items-center gap-3 flex-wrap">
                <h3 className="font-heading text-lg font-semibold truncate">{post.title}</h3>
                <span className="inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-bold border bg-slate-800 border-slate-700 text-slate-300 uppercase">
                  {post.lang || "fr"}{post.country ? ` · ${post.country}` : ""}
                </span>
                <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-bold border ${
                  post.published
                    ? "bg-green-600/15 border-green-600/30 text-green-400"
                    : "bg-slate-800 border-slate-700 text-[#93A0BC]"
                }`}>
                  {post.published ? <Eye className="w-3 h-3" /> : <EyeOff className="w-3 h-3" />}
                  {post.published ? t("Publié") : t("Brouillon")}
                </span>
              </div>
              <p className="mt-1 text-xs text-[#5C6A87]">
                /blog/{post.slug} · {new Date(post.created_at).toLocaleDateString("fr-BE")}
                {post.category ? ` · ${post.category}` : ""}
              </p>
            </div>
            <div className="flex gap-2 shrink-0">
              <button
                type="button"
                data-testid={`blog-edit-${post.slug}`}
                onClick={() => startEdit(post)}
                className="inline-flex items-center gap-2 rounded-full border border-slate-600 px-4 py-2 text-xs font-semibold text-slate-200 hover:border-slate-400 transition-colors duration-200"
              >
                <Pencil className="w-3.5 h-3.5" /> {t("Modifier")}
              </button>
              <button
                type="button"
                data-testid={`blog-delete-${post.slug}`}
                onClick={() => remove(post)}
                className="inline-flex items-center gap-2 rounded-full border border-red-900 px-4 py-2 text-xs font-semibold text-red-400 hover:border-red-500 transition-colors duration-200"
              >
                <Trash2 className="w-3.5 h-3.5" /> {t("Supprimer")}
              </button>
            </div>
          </article>
        ))}
      </div>
    </div>
  );
};
